/**
 * OrganizationsTab — the Organizations pane of the `/admin` portal.
 *
 * Composition (Carbon, mockup 02):
 *   1. OrgPendingQueue: a "Waiting for review" DataTable, only when there
 *      are pending rows. Reused from the legacy `/organization-management`
 *      page; we don't fork it.
 *   2. OrgListTable in portal mode: a DataTable whose status tabs
 *      (FilterChipRow, All / Active / Pending / Inactive) own this tab's
 *      filter key, with "Add organization" (the view's one primary button)
 *      in its toolbar. A row or its "Open" opens the right-side drawer;
 *      `onPivot` cross-navigates to People / Tenants.
 *   3. AdminDrawer + OrgDrawerContent: opens when `state.drawer` starts
 *      with `"org:"`. The drawer body owns the Edit / Invite / Approve /
 *      Deactivate flows and reaches back through this component for the
 *      mutation handles.
 *
 * State ownership:
 *   - Tab-local filter, drawer id, and cross-tab pivots live in the URL
 *     via `useAdminPortalState`. Deep links and the back button work for
 *     free.
 *   - Data + mutations come from `useOrganizations`. The hook surfaces
 *     `pendingCount` and `latestPendingName` directly; we don't recompute
 *     them locally.
 *   - No `<h1>` — the AdminPortalHeader (one level up) carries the page
 *     identity; this tab is just the content under the pill nav.
 */

import { useEffect, useMemo, useRef, useState } from 'react'
import toast from 'react-hot-toast'

import { useOrganizations } from '@/hooks/useOrganizations'
import { useAdminPortalState } from '@/hooks/useAdminPortalState'
import type {
  CreateOrganizationRequest,
  OrganizationListItemDto,
  UpdateOrganizationRequest,
} from '@/lib/api'

import { Button } from '@/components/carbon/Button'
import { ConfirmDialog } from '@/components/carbon/ConfirmDialog'
import { InlineNotification } from '@/components/carbon/InlineNotification'
import { IconAdd } from '@/components/carbon/icons'
import { orgRejectCopy } from '@/components/AdminPortal/confirm-copy'
import FilterChipRow, { type FilterChip } from '@/components/AdminPortal/FilterChipRow'
import OrgListTable from '@/components/OrganizationManagement/OrgListTable'
import OrgPendingQueue from '@/components/OrganizationManagement/OrgPendingQueue'
import OrgFormModal from '@/components/OrganizationManagement/OrgFormModal'
import AdminDrawer from '@/components/AdminPortal/AdminDrawer'
import OrgDrawerContent from '@/components/AdminPortal/OrgDrawerContent'

/* -------------------------------------------------------------------------- */
/* Filter helpers                                                             */
/* -------------------------------------------------------------------------- */

type FilterKey = 'all' | 'active' | 'pending' | 'inactive'

const VALID_FILTERS: ReadonlySet<string> = new Set(['all', 'active', 'pending', 'inactive'])

function parseFilter(raw: string | null): FilterKey {
  return raw && VALID_FILTERS.has(raw) ? (raw as FilterKey) : 'all'
}

function matchesFilter(row: OrganizationListItemDto, key: FilterKey): boolean {
  if (key === 'all') return true
  if (key === 'active') return row.status === 'Active'
  if (key === 'pending') return row.status === 'PendingApproval'
  // 'inactive' covers both Inactive and Rejected — same convention as
  // OrgListTable's legacy filter strip.
  return row.status === 'Inactive' || row.status === 'Rejected'
}

/* -------------------------------------------------------------------------- */
/* Drawer id helpers                                                          */
/* -------------------------------------------------------------------------- */

const ORG_DRAWER_PREFIX = 'org:'

function parseOrgDrawer(drawer: string | null): string | null {
  if (!drawer || !drawer.startsWith(ORG_DRAWER_PREFIX)) return null
  return drawer.slice(ORG_DRAWER_PREFIX.length)
}

/* -------------------------------------------------------------------------- */
/* Component                                                                  */
/* -------------------------------------------------------------------------- */

export default function OrganizationsTab() {
  const state = useAdminPortalState()
  const orgs = useOrganizations({ pageSize: 200 })

  // Memoise the rows array identity so downstream useMemos don't churn each
  // render (TanStack Query returns a fresh wrapper object even when items
  // are reference-equal).
  const rows = useMemo<OrganizationListItemDto[]>(
    () => orgs.data?.items ?? [],
    [orgs.data],
  )

  const activeFilter = parseFilter(state.filter)

  const counts = useMemo(() => ({
    all: rows.length,
    active: rows.filter(r => r.status === 'Active').length,
    pending: orgs.pendingCount,
    inactive: rows.filter(r => r.status === 'Inactive' || r.status === 'Rejected').length,
  }), [rows, orgs.pendingCount])

  const filteredRows = useMemo(
    () => rows.filter(r => matchesFilter(r, activeFilter)),
    [rows, activeFilter],
  )

  const pendingRows = useMemo(
    () => rows.filter(r => r.status === 'PendingApproval'),
    [rows],
  )

  // MSP + Platform rows feed the create-modal's parent-org dropdown. Customer
  // orgs must be parented to one of those two per OrganizationService rules.
  const msps = useMemo(
    () => rows.filter(r => (r.type === 'MSP' || r.type === 'Platform') && r.isActive),
    [rows],
  )

  // ── Create modal ──────────────────────────────────────────────────────────
  // Edit lives inside OrgDrawerContent; only create is owned here. State is
  // transient (never deep-linked) so it stays local rather than URL-bound,
  // mirroring MonitoredTenantsTab's "+ Add tenant" pattern.
  const [showAddModal, setShowAddModal] = useState(false)
  type SlugAvailability = 'unknown' | 'checking' | 'available' | 'taken'
  const [slugAvailability, setSlugAvailability] = useState<SlugAvailability>('unknown')

  const handleSlugCheck = (slug: string) => {
    if (!slug) return
    const collision = rows.some(r => r.slug === slug)
    setSlugAvailability(collision ? 'taken' : 'available')
  }

  const handleCreateSave = async (
    data: CreateOrganizationRequest | Partial<UpdateOrganizationRequest>,
  ) => {
    try {
      await orgs.createOrganization(data as CreateOrganizationRequest)
      toast.success('Organization created.')
      setShowAddModal(false)
      setSlugAvailability('unknown')
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Create failed')
    }
  }

  const chips: FilterChip[] = [
    { key: 'all', label: 'All', count: counts.all },
    { key: 'active', label: 'Active', count: counts.active },
    { key: 'pending', label: 'Pending', count: counts.pending },
    { key: 'inactive', label: 'Inactive', count: counts.inactive },
  ]

  /* ── Drawer resolution ─────────────────────────────────────────────────── */

  const drawerOrgId = parseOrgDrawer(state.drawer)
  // The drawer may target an org id that the current filter excludes — pull
  // the row out of the full list, not the filtered subset, so the drawer
  // keeps its target visible while the user toggles filters behind it.
  const drawerOrg = useMemo(
    () => (drawerOrgId ? rows.find(r => r.id === drawerOrgId) ?? null : null),
    [rows, drawerOrgId],
  )

  /* ── Mutation handlers ─────────────────────────────────────────────────── */
  // Toast wrappers keep failure surfaces consistent with the legacy
  // OrganizationManagement page. Approve / reject are also reachable from
  // OrgPendingQueue and from inside OrgDrawerContent — the handlers here
  // are the only ones that show toast feedback at the page level. The
  // drawer's own confirm dialogs already gate the user; we just show the
  // outcome.

  const handleApprove = async (row: OrganizationListItemDto) => {
    try {
      await orgs.approveOrganization(row.id, { allowAutoProvision: true })
      toast.success(`${row.name} approved.`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Approve failed')
    }
  }

  // Reject asks first: the ConfirmDialog (danger) names the row and stays
  // open, busy, until the request settles.
  const [rejectTarget, setRejectTarget] = useState<OrganizationListItemDto | null>(null)
  const [isRejecting, setIsRejecting] = useState(false)

  const handleReject = (row: OrganizationListItemDto) => setRejectTarget(row)

  const confirmReject = async (row: OrganizationListItemDto) => {
    setIsRejecting(true)
    try {
      // Dedicated reject endpoint — commits the honest 'Rejected' status
      // (deactivate writes 'Inactive', which broke the signup recycle path).
      await orgs.rejectOrganization(row.id, 'Rejected during intake review')
      toast.success(`${row.name} rejected.`)
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Reject failed')
    } finally {
      setIsRejecting(false)
      setRejectTarget(null)
    }
  }

  // ── Drawer-wrapped mutations ───────────────────────────────────────────
  // OrgDrawerContent expects mutations to return the row / Promise<void>.
  // We thread the hook's mutations through unchanged; the toast feedback
  // for in-drawer Approve / Deactivate happens here so the drawer body
  // stays focussed on its own confirm UX.

  const handleDrawerApprove = async (
    id: string,
    opts?: { allowAutoProvision?: boolean },
  ) => {
    const row = rows.find(r => r.id === id)
    const result = await orgs.approveOrganization(id, opts)
    if (row) toast.success(`${row.name} approved.`)
    return result
  }

  const handleDrawerDeactivate = async (id: string, reason?: string) => {
    const row = rows.find(r => r.id === id)
    await orgs.deactivateOrganization(id, reason)
    if (row) toast.success(`${row.name} deactivated.`)
  }

  const handleDrawerUpdate = orgs.updateOrganization

  /* ── Render ────────────────────────────────────────────────────────────── */

  const filterLabel = chips.find(c => c.key === activeFilter)?.label.toLowerCase() ?? 'all'

  return (
    <div className="flex flex-col gap-8">
      {/* A failed load sits above the queue so it's seen before the table. */}
      {orgs.error && (
        <InlineNotification kind="error" title="Organizations did not load.">
          {orgs.error.message}
        </InlineNotification>
      )}

      {/* Pending queue lives above the full table and is independent of the
          active filter: admins always want it visible while there's work
          to clear. The empty case for "no pending" is the queue's absence. */}
      {pendingRows.length > 0 && (
        <OrgPendingQueue
          rows={pendingRows}
          onApprove={handleApprove}
          onReject={handleReject}
        />
      )}

      {/* The full list, filtered by the status tabs. The tabs live inside the
          table (mockup 02), so they stay reachable when a filter is empty. */}
      <OrgListTable
        rows={filteredRows}
        description="Every access record, with its parent, the tenants it watches and the people who sign in from it."
        tabs={
          <FilterChipRow
            chips={chips}
            active={activeFilter}
            onChange={key => state.setFilter(key === 'all' ? null : key)}
            ariaLabel="Filter organizations by status"
          />
        }
        toolbar={
          <div className="ml-auto flex">
            <Button kind="primary" icon={<IconAdd />} onClick={() => setShowAddModal(true)}>
              Add organization
            </Button>
          </div>
        }
        isLoading={orgs.isLoading && !orgs.data}
        empty={
          rows.length === 0
            ? 'No organization has an access record yet. Add the first one above.'
            : `No ${filterLabel} organizations. Choose another status to see the rest.`
        }
        onOpen={row => state.setDrawer(`${ORG_DRAWER_PREFIX}${row.id}`)}
        onPivot={(target, orgId) =>
          state.mergePivot({
            tab: target === 'tenants' ? 'tenants' : 'people',
            orgFilter: orgId,
            // Clear any inherited filter from this tab so the destination
            // tab opens on its own default status rather than inheriting
            // "pending" / "inactive" from Organizations.
            filter: null,
          })
        }
      />

      {/* Drawer: mounted at the tab level so it survives filter changes
          and so OrgDrawerContent can call back into the mutation handles
          without re-resolving them through context.

          The `lastDrawerOrg` ref keeps the row body painted through the
          leave-transition: AdminDrawer drives slide-out off `isOpen`, but
          headless-ui still mounts children during the transition, so
          we need *something* to render. Clearing `state.drawer` (URL) and
          relying on a captured ref decouples URL state from animation. */}
      <OrganizationDrawer
        org={drawerOrg}
        onClose={() => state.setDrawer(null)}
        onPivot={(target, orgId) =>
          state.mergePivot({
            tab: target === 'tenants' ? 'tenants' : 'people',
            orgFilter: orgId,
            filter: null,
          })
        }
        updateOrganization={handleDrawerUpdate}
        deactivateOrganization={handleDrawerDeactivate}
        approveOrganization={handleDrawerApprove}
      />

      {showAddModal && (
        <OrgFormModal
          open={showAddModal}
          mode="create"
          initial={null}
          msps={msps}
          onSave={handleCreateSave}
          onClose={() => {
            setShowAddModal(false)
            setSlugAvailability('unknown')
          }}
          isSaving={orgs.isCreating}
          onSlugCheck={handleSlugCheck}
          slugAvailability={slugAvailability}
        />
      )}

      {rejectTarget && (
        <ConfirmDialog
          open
          {...orgRejectCopy(rejectTarget.name)}
          loading={isRejecting}
          onConfirm={() => void confirmReject(rejectTarget)}
          onCancel={() => setRejectTarget(null)}
        />
      )}
    </div>
  )
}

/* -------------------------------------------------------------------------- */
/* Drawer wrapper                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Wrapper around AdminDrawer + OrgDrawerContent. Owns the "render last org
 * during leave-transition" trick so the slide-out doesn't blank out the
 * drawer body the instant the URL state clears.
 *
 * `isOpen` is derived directly from `org !== null`. When the URL clears,
 * `org` goes null → `isOpen` goes false → headless-ui plays the 300ms slide
 * out. During that window we keep showing `lastOrg` (captured ref).
 */
function OrganizationDrawer({
  org,
  onClose,
  onPivot,
  updateOrganization,
  deactivateOrganization,
  approveOrganization,
}: {
  org: OrganizationListItemDto | null
  onClose(): void
  onPivot(target: 'tenants' | 'people', orgId: string): void
  updateOrganization: React.ComponentProps<typeof OrgDrawerContent>['updateOrganization']
  deactivateOrganization: React.ComponentProps<typeof OrgDrawerContent>['deactivateOrganization']
  approveOrganization: React.ComponentProps<typeof OrgDrawerContent>['approveOrganization']
}) {
  // Hold onto the most-recently-shown org so the body keeps painting during
  // the leave transition. We only update the ref while `org` is non-null —
  // once it goes null, the ref's existing value is what we render.
  const lastOrgRef = useRef<OrganizationListItemDto | null>(null)
  useEffect(() => {
    if (org) lastOrgRef.current = org
  }, [org])

  const displayOrg = org ?? lastOrgRef.current
  if (!displayOrg) return null

  const subtitle = `Organization · ${displayOrg.slug || displayOrg.id.slice(0, 8)}`

  return (
    <AdminDrawer
      isOpen={org !== null}
      onClose={onClose}
      title={displayOrg.name}
      subtitle={subtitle}
    >
      <OrgDrawerContent
        org={displayOrg}
        onClose={onClose}
        onPivot={onPivot}
        updateOrganization={updateOrganization}
        deactivateOrganization={deactivateOrganization}
        approveOrganization={approveOrganization}
      />
    </AdminDrawer>
  )
}
